import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight } from "@/components/icons";
import api from "@/lib/api";
import { ConectaAcenando } from "@/components/Conecta";
import { trancarPagina } from "@/lib/lockScroll";

// Cores dos "tom"s vindos do backend — combinam com o pedido.
const TOM_COR = {
  atraso: "#A8471C",
  info:   "#003E62",
  meta:   "#0E7A57",
};

export default function ResumoDoDia({ data, onClose }) {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const doneRef = useRef(false);
  const [closing, setClosing] = useState(false);
  const [rota, setRota] = useState(null);
  const linhas = Array.isArray(data?.linhas) ? data.linhas : [];
  const tudoEmDia = data?.tudo_em_dia === true;
  const primeiroNome = data?.primeiro_nome || "";

  // Marca no backend que o usuário já viu o resumo hoje (idempotente, silencioso).
  useEffect(() => {
    api.post("/resumo-dia/marcar", {}).catch(() => {});
  }, []);

  // Trava real de rolagem + foco preso ao painel + fecha com Esc.
  useEffect(() => {
    const el = panelRef.current;
    const liberar = trancarPagina();
    const firstBtn = el?.querySelector("button, a");
    firstBtn?.focus?.();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        iniciarSaida(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      liberar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const concluir = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (rota) navigate(rota);
    onClose?.();
  };

  // Rede de segurança: se o animationend não disparar, fecha depois de 700 ms.
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(concluir, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);

  const iniciarSaida = (link) => {
    setRota(link || null);
    setClosing(true);
  };

  const onVooAnimationEnd = (e) => {
    if (e.animationName === "cn-sai") concluir();
  };

  const ui = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="resumo-dia-titulo"
      data-testid="resumo-dia"
      className="fixed inset-0 z-[1000] flex items-end sm:items-center sm:justify-center"
      style={{ height: "100dvh", touchAction: "none", overflow: "hidden" }}
      onClick={(e) => { if (e.target === e.currentTarget) iniciarSaida(null); }}
    >
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity ${closing ? "opacity-0" : "opacity-100"}`}
        style={{ overscrollBehavior: "contain", touchAction: "none" }}
      />
      <div
        ref={panelRef}
        className={`relative w-full sm:w-[440px] sm:max-w-[440px] bg-white shadow-2xl transition-transform resumo-card ${closing ? "translate-y-full sm:translate-y-4 sm:opacity-0" : "translate-y-0"}`}
        style={{
          borderTopLeftRadius: 26, borderTopRightRadius: 26,
          borderBottomLeftRadius: typeof window !== "undefined" && window.innerWidth >= 640 ? 26 : 0,
          borderBottomRightRadius: typeof window !== "undefined" && window.innerWidth >= 640 ? 26 : 0,
          paddingBottom: "env(safe-area-inset-bottom)",
          // Sem overflow no cartão — a área que rola é `.resumo-conteudo` abaixo.
          // Assim o Conecta pode sair por cima sem ser cortado.
        }}
      >
        {/* Conecta 3D — FORA da área que rola. Estrutura de camadas do pedido. */}
        <div className="cn-slot cn-slot-resumo">
          <div
            className={`cn-voo${closing ? " saindo" : ""}`}
            onAnimationEnd={onVooAnimationEnd}
          >
            <div className="cn-gira">
              <div className="cn-tchau">
                <div className="cn-deriva">
                  <div className="cn-flutua">
                    <ConectaAcenando width={120} height={164} className="cn-img" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          data-testid="resumo-dia-fechar"
          aria-label="Fechar"
          onClick={() => iniciarSaida(null)}
          className="absolute top-3 right-3 h-11 w-11 flex items-center justify-center rounded-full hover:bg-slate-100 z-10"
        >
          <X className="w-5 h-5 text-navy" />
        </button>

        <div
          className="resumo-conteudo pt-24 px-5 pb-5"
          style={{ maxHeight: "100dvh", overflowY: "auto" }}
        >
          <h2 id="resumo-dia-titulo" className="text-xl font-bold text-navy" data-testid="resumo-dia-saudacao">
            {data?.saudacao || "Olá"}, {primeiroNome}!
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {tudoEmDia ? "Tudo em dia hoje. Boas vendas!" : "Aqui está o que pede atenção hoje:"}
          </p>

          {!tudoEmDia && (
            <ul className="mt-4 space-y-2" data-testid="resumo-dia-linhas">
              {linhas.map((linha) => (
                <li key={linha.id}>
                  <button
                    onClick={() => iniciarSaida(linha.link)}
                    data-testid={`resumo-linha-${linha.id}`}
                    className="w-full text-left flex items-center gap-3 rounded-2xl border border-black/5 bg-white hover:bg-slate-50 transition-colors px-4 py-3 min-h-[64px]"
                  >
                    <div
                      aria-hidden
                      className="w-1.5 rounded-full self-stretch shrink-0"
                      style={{ background: TOM_COR[linha.tom] || TOM_COR.info }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold text-navy leading-tight" style={{ color: TOM_COR[linha.tom] || TOM_COR.info }}>
                        {linha.texto}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{linha.detalhe}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 flex flex-col gap-2">
            <button
              onClick={() => iniciarSaida(data?.botao?.link || "/")}
              data-testid="resumo-dia-cta"
              className="w-full h-12 rounded-full text-white font-semibold text-[15px] shadow-sm transition-colors"
              style={{ background: "#003E62" }}
            >
              {data?.botao?.texto || "Continuar"}
            </button>
            <button
              onClick={() => iniciarSaida(null)}
              data-testid="resumo-dia-agora-nao"
              className="w-full h-12 rounded-full text-navy font-semibold text-[15px] hover:bg-slate-100"
            >
              Agora não
            </button>
          </div>

          <p className="mt-4 text-[11px] text-center text-muted-foreground leading-snug">
            Aparece só no primeiro acesso do dia · desligue em Configurações
          </p>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return ui;
  return createPortal(ui, document.body);
}
